import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import {
  Users, CalendarBlank, CurrencyDollar, ShieldCheck, Clock, Check, X, ChartBar,
  MagnifyingGlass, Trash, PencilSimple, Plus, Eye, WarningCircle, Star,
  ChatCircle, FileText, Funnel, ArrowRight, Lightning
} from '@phosphor-icons/react';
import { toast } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-amber-500/10 text-amber-600',
  confirmed: 'bg-secondary text-primary',
  completed: 'bg-emerald-500/10 text-emerald-600',
  cancelled: 'bg-red-500/10 text-red-600',
  approved: 'bg-emerald-500/10 text-emerald-600',
  banned: 'bg-red-500/10 text-red-600',
  rejected: 'bg-red-500/10 text-red-600',
  open: 'bg-amber-500/10 text-amber-600',
  'under review': 'bg-secondary text-primary',
  resolved: 'bg-emerald-500/10 text-emerald-600',
  initiated: 'bg-amber-500/10 text-amber-600',
  paid: 'bg-emerald-500/10 text-emerald-600',
  unpaid: 'bg-red-500/10 text-red-600',
  complete: 'bg-emerald-500/10 text-emerald-600',
  expired: 'bg-red-500/10 text-red-600',
};

function Badge({ status }) {
  return <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${STATUS_COLORS[status] || 'bg-secondary text-muted-foreground'}`}>{status}</span>;
}

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async (endpoint, setter) => {
    try { const r = await axios.get(`${API}${endpoint}`, { headers }); setter(r.data); }
    catch (err) { console.error(`Failed to load ${endpoint}:`, err); }
  }, [headers]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        load('/admin/analytics', setAnalytics),
        load('/admin/users', setUsers),
        load('/admin/providers', setVendors),
        load('/admin/activity', setActivity),
      ]);
      setLoading(false);
    };
    init();
  }, [load]);

  useEffect(() => {
    if (tab === 'services') load('/admin/services', setServices);
    if (tab === 'bookings') load('/admin/bookings', setBookings);
    if (tab === 'payments') load('/admin/payments', setPayments);
    if (tab === 'overview') load('/admin/activity', setActivity);
    if (tab === 'reviews') load('/admin/reviews', setReviews);
    if (tab === 'disputes') load('/admin/disputes', setDisputes);
    if (tab === 'messages') load('/admin/messages', setMessages);
    if (tab === 'audit') load('/admin/audit-log', setAuditLog);
    if (tab === 'verification') load('/admin/verifications', setVerifications);
  }, [tab, load]);

  const adminAction = async (method, endpoint, body = null) => {
    try {
      if (method === 'delete') await axios.delete(`${API}${endpoint}`, { headers });
      else if (method === 'put') await axios.put(`${API}${endpoint}`, body, { headers });
      else if (method === 'post') await axios.post(`${API}${endpoint}`, body, { headers });
      // Refresh
      load('/admin/analytics', setAnalytics);
      if (tab === 'vendors') load('/admin/providers', setVendors);
      if (tab === 'users') load('/admin/users', setUsers);
      if (tab === 'services') load('/admin/services', setServices);
      if (tab === 'bookings') load('/admin/bookings', setBookings);
      if (tab === 'payments') load('/admin/payments', setPayments);
    if (tab === 'payments') load('/admin/payments', setPayments);
      if (tab === 'reviews') load('/admin/reviews', setReviews);
      if (tab === 'disputes') load('/admin/disputes', setDisputes);
      if (tab === 'verification') load('/admin/verifications', setVerifications);
    } catch (err) { console.error('Admin action failed:', err); toast.error(err.response?.data?.detail || 'Action failed'); }
    setEditModal(null);
  };

  if (user?.role !== 'admin') {
    return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-lg mx-auto px-6 py-24 text-center"><h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Access Denied</h1></div></div>;
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: <ChartBar size={14} /> },
    { id: 'users', label: 'Users', icon: <Users size={14} /> },
    { id: 'vendors', label: 'Vendors', icon: <ShieldCheck size={14} /> },
    { id: 'services', label: 'Services', icon: <FileText size={14} /> },
    { id: 'bookings', label: 'Bookings', icon: <CalendarBlank size={14} /> },
    { id: 'payments', label: 'Payments', icon: <CurrencyDollar size={14} /> },
    { id: 'reviews', label: 'Reviews', icon: <Star size={14} /> },
    { id: 'disputes', label: 'Disputes', icon: <WarningCircle size={14} /> },
    { id: 'messages', label: 'Messages', icon: <ChatCircle size={14} /> },
    { id: 'verification', label: 'ID Verify', icon: <Eye size={14} /> },
    { id: 'audit', label: 'Audit Log', icon: <Clock size={14} /> },
  ];

  const filter = (list, fields) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(item => fields.some(f => {
      const val = f.split('.').reduce((o, k) => o?.[k], item);
      return val && String(val).toLowerCase().includes(q);
    }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Admin Dashboard</h1>
            <p className="text-xs text-muted-foreground mt-1">Bookr.ai Platform Management</p>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-accent-coral/10 text-accent-coral px-3 py-1 rounded-full">Admin</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-border mb-6">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 whitespace-nowrap transition-colors cursor-pointer ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Search bar for tables */}
        {tab !== 'overview' && (
          <div className="mb-4 relative max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="w-full h-11 pl-9 pr-3 text-sm bg-card border border-border rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="admin-search" />
          </div>
        )}

        {/* ═══ OVERVIEW ═══ */}
        {tab === 'overview' && analytics && (
          <div className="space-y-6" data-testid="admin-overview">
            {/* Live activity feed — everything happening on the platform */}
            {activity.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden fade-up" data-testid="activity-feed">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-secondary/40">
                  <Lightning size={13} weight="fill" className="text-primary" />
                  <p className="text-[11px] font-semibold text-foreground uppercase tracking-wider">Live activity</p>
                  <span className="text-[10px] text-muted-foreground ml-auto">latest {activity.length} events</span>
                </div>
                <div className="divide-y divide-border/50 max-h-72 overflow-y-auto">
                  {activity.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        a.type === 'payment' ? 'bg-emerald-500/10 text-emerald-600' :
                        a.type === 'provider' ? 'bg-amber-500/10 text-amber-600' :
                        a.type === 'booking' ? 'bg-secondary text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {a.type === 'payment' ? <CurrencyDollar size={13} weight="fill" /> :
                         a.type === 'provider' ? <ShieldCheck size={13} weight="fill" /> :
                         a.type === 'booking' ? <CalendarBlank size={13} weight="fill" /> : <Users size={13} weight="fill" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground truncate">{a.text}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{a.detail}</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">{(a.at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Bookings', value: analytics.total_bookings, sub: `${analytics.month_bookings} this month` },
                { label: 'Revenue', value: `$${(analytics.total_revenue || 0).toLocaleString()}`, sub: `$${(analytics.month_revenue || 0).toLocaleString()} this month` },
                { label: 'Platform Fees', value: `$${(analytics.total_fees || 0).toLocaleString()}`, sub: `$${(analytics.month_fees || 0).toLocaleString()} this month` },
                { label: 'Active Vendors', value: analytics.active_vendors, sub: `${analytics.pending_vendors} pending` },
                { label: 'Users', value: analytics.total_users, sub: 'Customers' },
                { label: 'Total Vendors', value: analytics.total_vendors, sub: `${analytics.active_vendors} active` },
                { label: 'Open Disputes', value: analytics.open_disputes, sub: 'Needs attention' },
                { label: 'This Month', value: analytics.month_bookings, sub: 'Bookings' },
              ].map((s, i) => (
                <div key={i} className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                  <p className="text-sm text-muted-foreground mb-1">{s.label}</p>
                  <p className="text-2xl font-display font-semibold text-foreground">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: '100ms' }}>
                <h3 className="font-display text-base font-semibold text-foreground mb-3">Top Categories</h3>
                {(analytics.top_categories || []).slice(0, 6).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between py-1.5 px-2 -mx-2 rounded-xl border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
                    <span className="text-xs text-foreground">{c.category}</span>
                    <span className="text-xs font-medium text-muted-foreground">{c.count} bookings</span>
                  </div>
                ))}
              </div>
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: '150ms' }}>
                <h3 className="font-display text-base font-semibold text-foreground mb-3">Vendors by City</h3>
                {(analytics.vendors_by_city || []).map((c, i) => (
                  <div key={c.city} className="flex items-center justify-between py-1.5 px-2 -mx-2 rounded-xl border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
                    <span className="text-xs text-foreground">{c.city}</span>
                    <span className="text-xs font-medium text-muted-foreground">{c.count} vendors</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ USERS ═══ */}
        {tab === 'users' && (
          <Table
            data={filter(users, ['name', 'email', 'role', 'city'])}
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'role', label: 'Role', render: (r) => <Badge status={r.role} /> },
              { key: 'setup', label: 'Vendor setup', render: (r) => r.role !== 'provider'
                ? <span className="text-muted-foreground/40">—</span>
                : r.has_vendor_profile
                  ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full"><Check size={11} /> Profile done</span>
                  : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full"><WarningCircle size={11} /> Didn't finish</span> },
              { key: 'city', label: 'City' },
              { key: 'created_at', label: 'Joined', render: (r) => <span className="text-[10px]">{r.created_at?.slice(0, 10)}</span> },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                <button onClick={() => adminAction('put', `/admin/users/${row.user_id}`, { role: row.role === 'provider' ? 'customer' : 'provider' })}
                  className="text-[10px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 px-2 py-1.5 border border-border rounded-lg btn-anim cursor-pointer">Toggle Role</button>
                <button onClick={() => { if (window.confirm('Delete this user?')) adminAction('delete', `/admin/users/${row.user_id}`); }}
                  className="text-[10px] text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer" aria-label="Delete user"><Trash size={11} /></button>
              </div>
            )}
          />
        )}

        {/* ═══ VENDORS ═══ */}
        {tab === 'vendors' && (
          <Table
            data={filter(vendors, ['business_name', 'category', 'city', 'status', 'user.email'])}
            columns={[
              { key: 'business_name', label: 'Company' },
              { key: 'category', label: 'Category' },
              { key: 'city', label: 'City' },
              { key: 'status', label: 'Status', render: (r) => <Badge status={r.status} /> },
              { key: 'email_verified', label: 'Email', render: (r) => r.user?.email_verified === false
                ? <span className="text-[10px] font-medium text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">Unverified</span>
                : <Check size={14} className="text-emerald-600" /> },
              { key: 'rating_avg', label: 'Rating', render: (r) => <span>{r.rating_avg}/5 ({r.review_count})</span> },
              { key: 'verified', label: 'Verified', render: (r) => r.verified ? <Check size={14} className="text-emerald-600" /> : <X size={14} className="text-muted-foreground/40" /> },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                {row.status !== 'approved' && <button onClick={() => adminAction('put', `/admin/providers/${row.profile_id}/status?status=approved`)} className="text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/10 px-2 py-1.5 border border-emerald-200 rounded-lg btn-anim cursor-pointer">Approve</button>}
                {row.status !== 'banned' && <button onClick={() => adminAction('put', `/admin/providers/${row.profile_id}/status?status=banned`)} className="text-[10px] font-medium text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer">Ban</button>}
                <button onClick={() => { if (window.confirm(`Set ${row.business_name} to CUSTOM QUOTE pricing? (no price shown — customers request a quote). This updates their profile and all their services.`)) adminAction('put', `/admin/vendors/${row.profile_id}/pricing`, { price_type: 'quote' }); }}
                  className="text-[10px] font-medium text-primary hover:bg-primary/10 px-2 py-1.5 border border-primary/30 rounded-lg btn-anim cursor-pointer" title="Switch this vendor to custom-quote pricing">Quote</button>
                <button onClick={() => { if (window.confirm('Delete vendor?')) adminAction('delete', `/admin/vendors/${row.profile_id}`); }}
                  className="text-[10px] text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer" aria-label="Delete vendor"><Trash size={11} /></button>
              </div>
            )}
          />
        )}

        {/* ═══ SERVICES ═══ */}
        {tab === 'services' && (
          <Table
            data={filter(services, ['title', 'category', 'provider_name'])}
            columns={[
              { key: 'title', label: 'Service' },
              { key: 'category', label: 'Category' },
              { key: 'provider_name', label: 'Vendor' },
              { key: 'base_price', label: 'Price', render: (r) => `$${r.base_price}` },
              { key: 'active', label: 'Active', render: (r) => r.active ? <Check size={14} className="text-emerald-600" /> : <X size={14} className="text-muted-foreground/40" /> },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                <button onClick={() => adminAction('put', `/admin/services/${row.service_id}`, { active: !row.active })}
                  className="text-[10px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 px-2 py-1.5 border border-border rounded-lg btn-anim cursor-pointer">{row.active ? 'Hide' : 'Show'}</button>
                <button onClick={() => { if (window.confirm('Delete service?')) adminAction('delete', `/admin/services/${row.service_id}`); }}
                  className="text-[10px] text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer" aria-label="Delete service"><Trash size={11} /></button>
              </div>
            )}
          />
        )}

        {/* ═══ BOOKINGS ═══ */}
        {tab === 'bookings' && (
          <Table
            data={filter(bookings, ['booking_id', 'category', 'status', 'booking_date', 'customer_name', 'provider_name', 'service_title'])}
            columns={[
              { key: 'booking_date', label: 'Date' },
              { key: 'customer_name', label: 'Customer', render: (r) => (<div><p className="font-medium">{r.customer_name || '—'}</p><p className="text-[10px] text-muted-foreground">{r.customer_email}</p></div>) },
              { key: 'provider_name', label: 'Vendor', render: (r) => r.provider_name || '—' },
              { key: 'service_title', label: 'Service', render: (r) => (<div><p>{r.service_title || r.category}</p><p className="text-[10px] text-muted-foreground">{r.category}</p></div>) },
              { key: 'total_paid', label: 'Total', render: (r) => <span className="font-semibold tabular-nums">${r.total_paid}</span> },
              { key: 'platform_fee', label: 'Fee', render: (r) => <span className="tabular-nums">${r.platform_fee}</span> },
              { key: 'status', label: 'Status', render: (r) => <Badge status={r.status} /> },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                {row.status === 'pending' && <button onClick={() => adminAction('put', `/admin/bookings/${row.booking_id}`, { status: 'confirmed' })} className="text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/10 px-2 py-1.5 border border-emerald-200 rounded-lg btn-anim cursor-pointer">Confirm</button>}
                {row.status !== 'cancelled' && <button onClick={() => adminAction('put', `/admin/bookings/${row.booking_id}`, { status: 'cancelled' })} className="text-[10px] font-medium text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer">Cancel</button>}
                <button
                  onClick={() => { if (window.confirm(`Permanently delete this booking (${row.customer_name || 'customer'} → ${row.provider_name || row.service_title || row.category})? This cannot be undone.`)) adminAction('delete', `/admin/bookings/${row.booking_id}`); }}
                  className="text-[10px] font-medium text-red-700 hover:bg-red-500/10 px-2 py-1.5 border border-red-300 rounded-lg btn-anim cursor-pointer"
                  title="Remove this booking entirely (for test/spam entries)">
                  Delete
                </button>
              </div>
            )}
          />
        )}

        {/* ═══ PAYMENTS ═══ */}
        {tab === 'payments' && (
          <Table
            data={filter(payments, ['customer_name', 'customer_email', 'session_id', 'payment_status'])}
            columns={[
              { key: 'created_at', label: 'Date', render: (r) => <span className="tabular-nums">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</span> },
              { key: 'customer_name', label: 'Customer', render: (r) => (<div><p className="font-medium">{r.customer_name || '—'}</p><p className="text-[10px] text-muted-foreground">{r.customer_email}</p></div>) },
              { key: 'amount', label: 'Amount', render: (r) => <span className="font-semibold tabular-nums">${Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} {(r.currency || 'cad').toUpperCase()}</span> },
              { key: 'booking_count', label: 'Bookings' },
              { key: 'payment_status', label: 'Status', render: (r) => <Badge status={r.payment_status} /> },
              { key: 'session_id', label: 'Stripe session', render: (r) => <span className="font-mono text-[10px]">{(r.session_id || '').slice(0, 24)}…</span> },
            ]}
          />
        )}

        {/* ═══ REVIEWS ═══ */}
        {tab === 'reviews' && (
          <Table
            data={filter(reviews, ['customer_name', 'provider_name', 'comment'])}
            columns={[
              { key: 'customer_name', label: 'Customer' },
              { key: 'provider_name', label: 'Vendor' },
              { key: 'rating', label: 'Rating', render: (r) => <span className="text-amber-400">{'★'.repeat(r.rating)}<span className="text-muted-foreground/40">{'☆'.repeat(5 - r.rating)}</span></span> },
              { key: 'comment', label: 'Comment', render: (r) => <span className="text-[11px] line-clamp-1 max-w-[200px]">{r.comment}</span> },
              { key: 'created_at', label: 'Date', render: (r) => r.created_at?.slice(0, 10) },
            ]}
            actions={(row) => (
              <button onClick={() => { if (window.confirm('Remove this review?')) adminAction('delete', `/admin/reviews/${row.review_id}`); }}
                className="text-[10px] font-medium text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer"><Trash size={11} /> Remove</button>
            )}
          />
        )}

        {/* ═══ DISPUTES ═══ */}
        {tab === 'disputes' && (
          <Table
            data={filter(disputes, ['dispute_id', 'description', 'status'])}
            columns={[
              { key: 'dispute_id', label: 'ID', render: (r) => <span className="font-mono text-[10px]">{r.dispute_id?.slice(0, 15)}</span> },
              { key: 'opener', label: 'Opened By', render: (r) => r.opener?.name || 'Unknown' },
              { key: 'description', label: 'Issue', render: (r) => <span className="text-[11px] line-clamp-1 max-w-[200px]">{r.description}</span> },
              { key: 'status', label: 'Status', render: (r) => <Badge status={r.status} /> },
              { key: 'created_at', label: 'Date', render: (r) => r.created_at?.slice(0, 10) },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                {row.status === 'open' && <button onClick={() => adminAction('put', `/admin/disputes/${row.dispute_id}`, { status: 'under review' })} className="text-[10px] font-medium text-primary hover:bg-secondary px-2 py-1.5 border border-border rounded-lg btn-anim cursor-pointer">Review</button>}
                {row.status !== 'resolved' && <button onClick={() => adminAction('put', `/admin/disputes/${row.dispute_id}`, { status: 'resolved', resolution: 'Resolved by admin' })} className="text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/10 px-2 py-1.5 border border-emerald-200 rounded-lg btn-anim cursor-pointer">Resolve</button>}
                {row.status !== 'resolved' && <button onClick={() => { if (window.confirm('Issue refund?')) adminAction('put', `/admin/disputes/${row.dispute_id}`, { status: 'resolved', refund: true, booking_id: row.booking_id }); }} className="text-[10px] font-medium text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer">Refund</button>}
              </div>
            )}
          />
        )}

        {/* ═══ MESSAGES ═══ */}
        {tab === 'messages' && (
          <Table
            data={filter(messages, ['sender_id', 'recipient_id', 'body']).slice(0, 100)}
            columns={[
              { key: 'sender_id', label: 'Sender', render: (r) => <span className="font-mono text-[10px]">{r.sender_id?.slice(0, 15)}</span> },
              { key: 'recipient_id', label: 'Recipient', render: (r) => <span className="font-mono text-[10px]">{r.recipient_id?.slice(0, 15)}</span> },
              { key: 'body', label: 'Message', render: (r) => <span className="text-[11px] line-clamp-1 max-w-[300px]">{r.body}</span> },
              { key: 'blocked_for_contact_sharing', label: 'Blocked', render: (r) => r.blocked_for_contact_sharing ? <WarningCircle size={14} className="text-red-500" /> : null },
              { key: 'created_at', label: 'Time', render: (r) => r.created_at?.slice(0, 16) },
            ]}
          />
        )}

        {/* ═══ VERIFICATION ═══ */}
        {tab === 'verification' && (
          <Table
            data={filter(verifications, ['provider.business_name', 'document_type', 'status'])}
            columns={[
              { key: 'provider', label: 'Vendor', render: (r) => r.provider?.business_name || r.user?.name || 'Unknown' },
              { key: 'document_type', label: 'Document', render: (r) => r.document_type?.replace(/_/g, ' ') },
              { key: 'status', label: 'Status', render: (r) => <Badge status={r.status} /> },
              { key: 'submitted_at', label: 'Submitted', render: (r) => r.submitted_at?.slice(0, 10) },
            ]}
            actions={(row) => (
              <div className="flex gap-1">
                {row.status === 'pending' && <button onClick={() => adminAction('put', `/admin/verifications/${row.verification_id}?action=approved`)} className="text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/10 px-2 py-1.5 border border-emerald-200 rounded-lg btn-anim cursor-pointer">Approve</button>}
                {row.status === 'pending' && <button onClick={() => adminAction('put', `/admin/verifications/${row.verification_id}?action=rejected&reason=Unclear+photo`)} className="text-[10px] font-medium text-red-600 hover:bg-red-500/10 px-2 py-1.5 border border-red-200 rounded-lg btn-anim cursor-pointer">Reject</button>}
              </div>
            )}
          />
        )}

        {/* ═══ AUDIT LOG ═══ */}
        {tab === 'audit' && (
          <Table
            data={filter(auditLog, ['action', 'admin_id'])}
            columns={[
              { key: 'action', label: 'Action' },
              { key: 'admin_id', label: 'Admin', render: (r) => <span className="font-mono text-[10px]">{r.admin_id?.slice(0, 15)}</span> },
              { key: 'details', label: 'Details', render: (r) => <span className="text-[10px] line-clamp-1 max-w-[250px]">{JSON.stringify(r.details)}</span> },
              { key: 'timestamp', label: 'Time', render: (r) => r.timestamp?.slice(0, 16) },
            ]}
          />
        )}
      </div>
    </div>
  );
}

// ─── Reusable Table Component ───
function Table({ data, columns, actions }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-12 flex flex-col items-center text-center fade-up">
        <div className="bg-secondary rounded-full p-4 mb-4">
          <MagnifyingGlass size={24} className="text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">Nothing here yet — records will appear as soon as there's activity.</p>
      </div>
    );
  }
  return (
    <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden overflow-x-auto fade-up">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 border-b border-border">
          <tr>
            {columns.map(col => (
              <th key={col.key} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{col.label}</th>
            ))}
            {actions && <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.user_id || row.profile_id || row.service_id || row.booking_id || row.review_id || row.dispute_id || row.verification_id || row.log_id || row.message_id || i}
                className="border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
              {columns.map(col => (
                <td key={col.key} className="px-4 py-2.5 text-xs text-foreground whitespace-nowrap">
                  {col.render ? col.render(row) : (row[col.key] ?? '—')}
                </td>
              ))}
              {actions && <td className="px-4 py-2.5">{actions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-border/60 text-[10px] text-muted-foreground">{data.length} records</div>
    </div>
  );
}
